import PropTypes from 'prop-types';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// material-ui
import { useTheme, styled } from '@mui/material/styles';
import { Avatar, Box, List, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import TotalIncomeCard from 'ui-component/cards/Skeleton/TotalIncomeCard';
import User1 from 'assets/images/users/user-round.svg';
import BASE_URL from '../../../redux/config';
// assets
import StorefrontTwoToneIcon from '@mui/icons-material/StorefrontTwoTone';
import { SET_MENU, SET_SIDE_MENU } from 'store/actions';
import { SET_ADVERTISER } from '../../../redux/advertiser/advertiserTypes';
// styles
const CardWrapper = styled(MainCard)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  overflow: 'hidden',
  position: 'relative'
}));

// ==============================|| DASHBOARD - TOTAL INCOME LIGHT CARD ||============================== //

const AdvertiserCard = ({ data, isLoading, user_type }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const openDashboardForAdvertiser = (data) => {
    console.log('Clicked advertiser:', data);

    // CREATE UNIQUE ADVERTISER KEY (GUID + SFID or just GUID if no SFID)
    const sfId = data.salesforce_account_id || '';
    const advertiserKey = sfId ? `${data.advertiser_guid}_${sfId}` : data.advertiser_guid;

    console.log('Generated advertiserKey:', advertiserKey);
    console.log('SFID:', sfId);

    dispatch({ type: SET_SIDE_MENU, showSideMenu: false });

    // CLEAR OLD DATA FIRST
    localStorage.removeItem('advertiserKey');
    localStorage.removeItem('advertiserId');
    localStorage.removeItem('sfId');
    localStorage.removeItem('timeZone');
    localStorage.removeItem('startDate');
    localStorage.removeItem('platform');

    // SET NEW DATA
    localStorage.setItem('showSideMenu', false);
    localStorage.setItem('advertiserKey', advertiserKey); // This is the key addition
    localStorage.setItem('advertiserId', data.advertiser_guid);
    localStorage.setItem('sfId', sfId); // Always set, even if empty
    localStorage.setItem('timeZone', data.timezone);
    localStorage.setItem('startDate', data.start_date);
    localStorage.setItem('platform', JSON.stringify(data.platform));

    let advData = {
      advertiser_guid: data.advertiser_guid,
      advertiser_name: data.advertiser_name,
      sfId: sfId,
      timeZone: data.timezone,
      startDate: data.start_date,
      platform: data.platform
    };

    dispatch({ type: SET_ADVERTISER, payload: advData });

    // Platform-based redirect
    const activePlatform = data.platform;
    if (activePlatform) {
      const hasDSP = activePlatform.includes('DSP');
      const hasIAP = activePlatform.includes('IAP');
      const hasD2C = activePlatform.includes('D2C');
      const hasCTV = activePlatform.includes('CTV');
      const hasGTV = activePlatform.includes('GTV');

      if (hasDSP || (hasIAP && (hasD2C || hasCTV || hasGTV))) {
        console.log('AdvCard Redirect Dashboard ===>', activePlatform);
        window.location.href = `${BASE_URL}/dashboard`;
      } else if (hasIAP) {
        console.log('AdvCard Redirect IAP only ===>', activePlatform);
        window.location.href = `${BASE_URL}/iap`;
      } else if (hasD2C) {
        console.log('AdvCard Redirect D2C only ===>', activePlatform);
        window.location.href = `${BASE_URL}/d2c`;
      } else if (hasCTV) {
        console.log('AdvCard Redirect CTV only ===>', activePlatform);
        window.location.href = `${BASE_URL}/ctv`;
      } else if (hasGTV) {
        console.log('AdvCard Redirect GTV only ===>', activePlatform);
        window.location.href = `${BASE_URL}/gtv`;
      } else {
        console.log('AdvCard Redirect Default Dashboard ===>', activePlatform);
        window.location.href = `${BASE_URL}/dashboard`;
      }
    }
  };

  // Check if user should see SFID
  const shouldShowSfid = () => {
    return user_type === 'admin' || user_type === 'internal';
  };

  return (
    <>
      {isLoading ? (
        <TotalIncomeCard />
      ) : (
        <CardWrapper
          border={true}
          content={false}
          sx={{
            backgroundColor: theme.palette.primary.light,
            '&:hover': {
              borderColor: theme.palette.primary.dark
            }
          }}
          onClick={() => openDashboardForAdvertiser(data)}
        >
          <Box sx={{ p: 2 }}>
            <List sx={{ py: 0 }}>
              <ListItem alignItems="center" disableGutters sx={{ py: 0 }}>
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={data.logo}
                    sx={{
                      ...theme.typography.commonAvatar,
                      ...theme.typography.largeAvatar,
                      backgroundColor: theme.palette.dark.light,
                      color: theme.palette.text.primary
                    }}
                  >
                    {data.advertiser_name.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  sx={{
                    py: 0,
                    mt: 0.45,
                    mb: 0.45,
                    '&:hover': {
                      color: 'white'
                    }
                  }}
                  primary={<Typography variant="h4">{data.advertiser_name}</Typography>}
                  // Show SFID for Admin, Internal
                  secondary={
                    shouldShowSfid() ? (
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: '#0a50d2ff',
                          mt: 0.5
                        }}
                      >
                        {data.salesforce_account_id}
                      </Typography>
                    ) : null
                  }
                />
                <ListItemText
                  style={{
                    float: 'right',
                    top: '-16px',
                    position: 'absolute',
                    right: 0,
                    fontSize: 10,
                    fontWeight: 'normal',
                    color: 'green'
                  }}
                  primary={
                    <Typography variant="h6" style={{ color: 'green' }}>
                      {data.timezone}
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>
        </CardWrapper>
      )}
    </>
  );
};

AdvertiserCard.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.object.isRequired,
  user_type: PropTypes.string
};

export default AdvertiserCard;
